import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarDays, Clock, CheckCircle, XCircle, AlertCircle, MessageSquarePlus,
  Check, X, Filter, User as UserIcon, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  returned_for_revision: 'bg-orange-100 text-orange-700',
};
const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle, returned_for_revision: AlertCircle };
const LEAVE_TYPE_LABEL = { pto: 'PTO / Vacation', sick: 'Sick Leave', emergency: 'Emergency' };

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return d || '—'; } };
const fmtRel = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const LeaveApprovalsPage = () => {
  const { user } = useAuth();
  const canApprove = user?.role === 'company_admin' || user?.role === 'super_admin';
  const isDispatcher = user?.role === 'dispatcher';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});      // id → draft note
  const [actionBusy, setActionBusy] = useState(null);  // `${id}:${action}` while in-flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leave-requests`, { withCredentials: true });
      // Exclude current user's own requests — those live on /my-leave
      const myUid = user?.id;
      const others = (res.data || []).filter((l) => l.user_id !== myUid);
      setItems(others);
    } catch (e) {
      toast.error('Failed to load leave requests');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((l) => filter === 'all' || l.status === filter);
  const counts = items.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, {});

  const doAction = async (id, action, payload = {}) => {
    setActionBusy(`${id}:${action}`);
    try {
      const path = action === 'note' ? 'note' : action;
      await axios.post(`${API}/leave-requests/${id}/${path}`, payload, { withCredentials: true });
      toast.success({
        approve: 'Leave approved',
        reject: 'Leave denied',
        note: 'Note added',
      }[action]);
      if (action === 'note') setNoteDraft((d) => ({ ...d, [id]: '' }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action}`);
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="leave-approvals-page">
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Leave Requests
          </h1>
          <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {canApprove
              ? 'Review and approve or deny pending leave from your team.'
              : 'Add notes for the admin to consider when reviewing leave requests.'}
          </p>
        </header>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {[
            { key: 'pending',  label: 'Pending' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Denied' },
            { key: 'all',      label: 'All' },
          ].map((f) => {
            const active = filter === f.key;
            const cnt = f.key === 'all' ? items.length : (counts[f.key] || 0);
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`leave-filter-${f.key}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors flex items-center gap-2 ${
                  active ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                }`}
              >
                <Filter className="w-3 h-3" />
                {f.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/30' : 'bg-slate-100 text-slate-700'}`}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="leave-empty-state">
            <CalendarDays className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              No {filter !== 'all' ? filter : ''} leave requests right now.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="leave-list">
            {filtered.map((l) => {
              const StatusIcon = STATUS_ICON[l.status] || Clock;
              const expanded = expandedId === l.id;
              const dnotes = l.dispatcher_notes || [];
              return (
                <li
                  key={l.id}
                  data-testid={`leave-row-${l.id}`}
                  className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 bg-[#002FA7]/10 rounded-md flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-4 h-4 text-[#002FA7]" strokeWidth={2.25} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{l.driver_name || l.user_name || 'Driver'}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type} · {fmtDate(l.start_date)} → {fmtDate(l.end_date)}
                        </p>
                        {l.reason && <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">"{l.reason}"</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold ${STATUS_PILL[l.status] || 'bg-slate-100 text-slate-700'}`}>
                        <StatusIcon className="w-3 h-3" />
                        {l.status}
                      </span>
                      {dnotes.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700">
                          <MessageSquarePlus className="w-3 h-3" />
                          {dnotes.length}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <button
                      onClick={() => setExpandedId(expanded ? null : l.id)}
                      data-testid={`leave-toggle-${l.id}`}
                      className="text-xs text-[#002FA7] hover:underline font-semibold"
                    >
                      {expanded ? 'Collapse' : (dnotes.length > 0 ? `View ${dnotes.length} note${dnotes.length !== 1 ? 's' : ''}` : 'Add a note')}
                    </button>
                    {canApprove && l.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => doAction(l.id, 'reject', { note: noteDraft[l.id] || '' })}
                          disabled={actionBusy === `${l.id}:reject`}
                          data-testid={`leave-deny-${l.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs font-semibold disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" /> Deny
                        </button>
                        <button
                          onClick={() => doAction(l.id, 'approve', { note: noteDraft[l.id] || '' })}
                          disabled={actionBusy === `${l.id}:approve`}
                          data-testid={`leave-approve-${l.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                        >
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                      </div>
                    )}
                  </div>

                  {expanded && (
                    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`leave-expanded-${l.id}`}>
                      {/* Existing notes */}
                      {dnotes.length > 0 && (
                        <ul className="space-y-2 mb-4">
                          {dnotes.map((n, idx) => (
                            <li key={idx} className="bg-slate-50 rounded-md p-3 text-xs border border-slate-100">
                              <div className="flex items-baseline justify-between mb-1">
                                <span className="font-semibold text-slate-900">
                                  {n.by_name}
                                  <span className="text-slate-400 font-normal ml-1.5 text-[10px] uppercase tracking-wider">{n.by_role}</span>
                                </span>
                                <span className="text-[10px] text-slate-400">{fmtRel(n.at)}</span>
                              </div>
                              <p className="text-slate-700 whitespace-pre-wrap">{n.note}</p>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Add-note input — visible to dispatcher OR admin, only while pending */}
                      {l.status === 'pending' && (isDispatcher || canApprove) && (
                        <div className="space-y-2">
                          <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            {isDispatcher ? 'Note for admin' : 'Note (optional, attached to your decision)'}
                          </label>
                          <textarea
                            data-testid={`leave-note-input-${l.id}`}
                            value={noteDraft[l.id] || ''}
                            onChange={(e) => setNoteDraft((d) => ({ ...d, [l.id]: e.target.value }))}
                            placeholder={isDispatcher ? 'e.g. "Two other drivers also off this week — coverage may be tight."' : 'e.g. "Approved — coverage already arranged."'}
                            className="w-full min-h-[80px] text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40"
                          />
                          {isDispatcher && (
                            <div className="flex justify-end">
                              <button
                                onClick={() => doAction(l.id, 'note', { note: (noteDraft[l.id] || '').trim() })}
                                disabled={!(noteDraft[l.id] || '').trim() || actionBusy === `${l.id}:note`}
                                data-testid={`leave-note-submit-${l.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#002FA7] hover:bg-[#00247D] text-white text-xs font-semibold disabled:opacity-50"
                              >
                                {actionBusy === `${l.id}:note` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />}
                                Add note
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LeaveApprovalsPage;
